import { useEffect } from "react";
import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo";
import { trackGA4Event } from "@/lib/ga4";

const Privacy = () => {
  useEffect(() => {
    document.title = "Privacy Policy | Prime Realty Partners - New Jersey Real Estate";
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Privacy Policy"
        description="Read Prime Realty Partners' privacy policy. Learn how we collect, use, and protect your personal information when you use our website and services."
        canonicalUrl="https://www.prpsj.com/privacy"
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Privacy Policy", url: "https://www.prpsj.com/privacy" }]}
      />

      <div className="container py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground mb-8">
            Effective Date: December 29, 2025
          </p>

          <div className="prose prose-lg max-w-none space-y-8">
            <p className="text-muted-foreground">
              MarChris Ventures LLC, doing business as Prime Realty Partners ("Company," "we," "us," or "our"), 
              respects your privacy and is committed to protecting it through this Privacy Policy. This policy 
              explains how we collect, use, disclose, and safeguard your information when you visit our website 
              https://PRPSJ.com, use our services, or communicate with us via phone, text message, email, or 
              other electronic means.
            </p>
            <p className="text-muted-foreground font-medium">
              By accessing or using our website or services, you agree to the terms of this Privacy Policy.
            </p>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                1. Information We Collect
              </h2>
              
              <h3 className="font-heading text-xl font-medium mb-3">Personal Information</h3>
              <p className="text-muted-foreground mb-4">
                We may collect personal information that you voluntarily provide to us, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-6">
                <li>Full name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Property address</li>
                <li>Buying or selling preferences</li>
                <li>Information submitted through contact forms, lead forms, inquiries, or direct communications</li>
              </ul>

              <h3 className="font-heading text-xl font-medium mb-3">Automatically Collected Information</h3>
              <p className="text-muted-foreground mb-4">
                When you visit our website, we may automatically collect:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>IP address</li>
                <li>Browser type and device information</li>
                <li>Pages visited and time spent on the site</li>
                <li>Referring URLs</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                2. How We Use Your Information
              </h2>
              <p className="text-muted-foreground mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Provide real estate brokerage and related services</li>
                <li>Respond to inquiries and requests for information</li>
                <li>Send property listings, market updates, and real estate communications</li>
                <li>Schedule consultations, showings, and appointments</li>
                <li>Communicate via phone calls, emails, and text messages</li>
                <li>Improve website performance and user experience</li>
                <li>Comply with legal and regulatory obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                3. SMS & Text Message Communications
              </h2>
              <p className="text-muted-foreground mb-4">
                By providing your phone number, you consent to receive SMS and MMS messages from Prime Realty Partners 
                related to real estate services, including property alerts, appointment confirmations, follow-up 
                communications, and transactional updates. Messages may be sent using automated technology.
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Message frequency varies</li>
                <li>Message and data rates may apply</li>
                <li>You may opt out at any time by replying STOP</li>
                <li>For assistance, reply HELP or contact us directly</li>
              </ul>
              <p className="text-muted-foreground font-medium">
                Consent to receive text messages is not a condition of purchase.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                4. Sharing of Your Information
              </h2>
              <p className="text-muted-foreground mb-4 font-medium">
                We do not sell your personal information.
              </p>
              <p className="text-muted-foreground mb-4">
                We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>With service providers (CRM platforms, communication providers, hosting services) solely to perform business functions on our behalf</li>
                <li>With real estate transaction partners (lenders, title companies, attorneys, inspectors) when necessary to facilitate a transaction or at your request</li>
                <li>To comply with legal obligations, court orders, or lawful government requests</li>
                <li>To protect the rights, property, or safety of Prime Realty Partners, our clients, or others</li>
              </ul>
              <p className="text-muted-foreground">
                All third parties are required to maintain the confidentiality and security of your information.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                5. Cookies & Tracking Technologies
              </h2>
              <p className="text-muted-foreground mb-4">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Analyze website traffic and performance</li>
                <li>Improve website functionality and user experience</li>
                <li>Support marketing, analytics, and remarketing efforts</li>
              </ul>
              <p className="text-muted-foreground">
                You may disable cookies through your browser settings; however, doing so may limit certain website features.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                6. Third-Party Websites
              </h2>
              <p className="text-muted-foreground">
                Our website may contain links to third-party websites. We are not responsible for the content, 
                security, or privacy practices of those websites. We encourage you to review their privacy 
                policies independently.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                7. Data Security
              </h2>
              <p className="text-muted-foreground">
                We implement reasonable administrative, technical, and physical safeguards designed to protect 
                your personal information. However, no method of transmission over the internet or electronic 
                storage is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                8. Data Retention
              </h2>
              <p className="text-muted-foreground">
                We retain personal information only for as long as reasonably necessary to fulfill the purposes 
                outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce agreements.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                9. Your Privacy Rights
              </h2>
              <p className="text-muted-foreground mb-4">
                Depending on applicable laws, you may have the right to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Request access to your personal information</li>
                <li>Request correction or deletion of your personal information</li>
                <li>Withdraw consent for certain communications</li>
              </ul>
              <p className="text-muted-foreground">
                Requests may be submitted using the contact information below.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                10. Children's Privacy
              </h2>
              <p className="text-muted-foreground">
                Our website and services are not intended for individuals under the age of 18. We do not 
                knowingly collect personal information from children.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                11. Changes to This Privacy Policy
              </h2>
              <p className="text-muted-foreground">
                We reserve the right to update this Privacy Policy at any time. Any changes will be posted 
                on this page with a revised effective date. Continued use of our website constitutes 
                acceptance of any changes.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                12. Contact Information
              </h2>
              <p className="text-muted-foreground mb-4">
                If you have questions or concerns regarding this Privacy Policy or our data practices, 
                please contact us:
              </p>
              <div className="text-muted-foreground space-y-1">
                <p className="font-medium">MarChris Ventures LLC</p>
                <p>dba Prime Realty Partners</p>
                <p>31 Walnut Ave</p>
                <p>Bellmawr, NJ 08031</p>
                <p>Email: <a href="mailto:info@prpsj.com" className="text-primary hover:underline" onClick={() => trackGA4Event('email_click', { event_category: 'lead_action', email: 'info@prpsj.com' })}>info@prpsj.com</a></p>
                <p>Website: <a href="https://PRPSJ.com" className="text-primary hover:underline">https://PRPSJ.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Privacy;
